/**
 * src/components/layout/Sidebar.jsx
 * Barra lateral de navegación — diseño industrial dark.
 */
import { NavLink, useNavigate } from 'react-router-dom'
import { useAuthStore } from '../../store/authStore'

const NAV_ITEMS = [
  {
    to: '/', label: 'Dashboard', exact: true,
    icon: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
        <rect x="3" y="3" width="7" height="7" rx="1" />
        <rect x="14" y="3" width="7" height="7" rx="1" />
        <rect x="14" y="14" width="7" height="7" rx="1" />
        <rect x="3" y="14" width="7" height="7" rx="1" />
      </svg>
    ),
  },
  {
    to: '/clientes', label: 'Clientes',
    icon: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
      </svg>
    ),
  },
  {
    to: '/settings/billing', label: 'Facturación',
    icon: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
        <rect x="1" y="4" width="22" height="16" rx="2" ry="2" />
        <line x1="1" y1="10" x2="23" y2="10" />
      </svg>
    ),
  },
]

function NavItem({ to, label, icon, exact }) {
  return (
    <NavLink
      to={to}
      end={exact}
      style={({ isActive }) => ({
        display: 'flex', alignItems: 'center', gap: '10px',
        padding: '9px 12px', borderRadius: '7px',
        textDecoration: 'none',
        fontSize: '13px', fontWeight: isActive ? 500 : 400,
        color: isActive ? 'var(--color-accent)' : 'var(--color-text-2)',
        background: isActive ? 'var(--color-accent-dim)' : 'transparent',
        border: isActive ? '1px solid rgba(16,185,129,0.15)' : '1px solid transparent',
        transition: 'all 0.15s',
      })}
      onMouseEnter={e => {
        const a = e.currentTarget
        if (!a.classList.contains('active'))
          a.style.background = 'var(--color-surface-2)'
      }}
      onMouseLeave={e => {
        const a = e.currentTarget
        if (!a.classList.contains('active'))
          a.style.background = 'transparent'
      }}
    >
      {icon}
      {label}
    </NavLink>
  )
}

export function Sidebar({ activeJobs = 0 }) {
  const logout = useAuthStore(state => state.logout)
  const navigate = useNavigate()

  function handleLogout() {
    logout()
    navigate('/login', { replace: true })
  }

  return (
    <aside style={{
      width: '220px', flexShrink: 0,
      background: 'var(--color-surface)',
      borderRight: '1px solid var(--color-border)',
      display: 'flex', flexDirection: 'column',
      height: '100vh', position: 'sticky', top: 0,
    }}>
      {/* Logo */}
      <div style={{
        padding: '20px 16px 16px',
        borderBottom: '1px solid var(--color-border)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '9px' }}>
          <div style={{
            width: '28px', height: '28px', borderRadius: '6px',
            background: 'var(--color-accent)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
          }}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5">
              <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
            </svg>
          </div>
          <div>
            <div style={{
              fontSize: '14px', fontWeight: 600, color: 'var(--color-text)',
              fontFamily: 'var(--font-mono)', letterSpacing: '-0.02em',
            }}>
              CM Monitor
            </div>
            <div style={{
              fontSize: '10px', color: 'var(--color-text-3)',
              letterSpacing: '0.06em', marginTop: '1px',
            }}>
              v3.0
            </div>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <nav style={{ flex: 1, padding: '12px 8px', display: 'flex', flexDirection: 'column', gap: '2px' }}>
        {NAV_ITEMS.map(item => (
          <NavItem key={item.to} {...item} />
        ))}
      </nav>

      {/* Scheduler status indicator */}
      <div style={{
        padding: '12px 16px',
        borderTop: '1px solid var(--color-border)',
      }}>
        <div style={{
          display: 'flex', alignItems: 'center', gap: '8px',
          padding: '8px 10px', borderRadius: '6px',
          background: 'var(--color-surface-2)',
          border: '1px solid var(--color-border)',
        }}>
          <span style={{
            width: '7px', height: '7px', borderRadius: '50%', flexShrink: 0,
            background: activeJobs > 0 ? 'var(--color-ok)' : 'var(--color-text-3)',
            ...(activeJobs > 0 ? { animation: 'pulseDot 1.4s ease-in-out infinite' } : {}),
          }} />
          <div>
            <div style={{
              fontSize: '11px', color: 'var(--color-text-2)',
              fontFamily: 'var(--font-mono)',
            }}>
              {activeJobs > 0 ? `${activeJobs} job${activeJobs > 1 ? 's' : ''} activo${activeJobs > 1 ? 's' : ''}` : 'Scheduler inactivo'}
            </div>
          </div>
        </div>
      </div>

      {/* Logout */}
      <div style={{ padding: '8px 8px 16px' }}>
        <button
          onClick={handleLogout}
          style={{
            width: '100%', display: 'flex', alignItems: 'center', gap: '9px',
            padding: '9px 12px', borderRadius: '7px',
            border: '1px solid transparent', background: 'transparent',
            color: 'var(--color-text-3)', cursor: 'pointer',
            fontSize: '13px', fontFamily: 'var(--font-ui)',
            transition: 'all 0.15s',
          }}
          onMouseEnter={e => {
            e.currentTarget.style.background = 'rgba(239,68,68,0.08)'
            e.currentTarget.style.color = 'var(--color-error)'
          }}
          onMouseLeave={e => {
            e.currentTarget.style.background = 'transparent'
            e.currentTarget.style.color = 'var(--color-text-3)'
          }}
        >
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
            <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
            <polyline points="16 17 21 12 16 7" />
            <line x1="21" y1="12" x2="9" y2="12" />
          </svg>
          Cerrar sesión
        </button>
      </div>
    </aside>
  )
}
